import { useState } from "react";
import { Check } from "lucide-react";
import styled from "styled-components";
import { AppButton } from "@/shared/components";
import { MOBILE_GUTTER, SAFE_AREA, SHEET_RADIUS, TAP_TARGET_PRIMARY, mediaPhone } from "@/shared/theme/breakpoints";
import { clearSignUpSuccessPending, getSignUpSuccessPending } from "../utils/signupSuccessSession";
import { pressedFade } from "@/shared/theme/pressed";

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2202;
	display: grid;
	place-items: center;
	padding: 24px;
	background: rgba(0, 0, 0, 0.45);

	${mediaPhone`
		place-items: end center;
		padding: 0;
	`}
`;
const Dialog = styled.section`
	width: min(568px, 100%);
	padding: 32px;
	border-radius: 24px;
	background: #f3f5ff;
	box-shadow: 0 12px 24px rgba(21, 89, 165, 0.24);
	text-align: center;

	/* Phone: a bottom sheet like DynamicModal's. */
	${mediaPhone`
		width: 100%;
		padding: 32px ${MOBILE_GUTTER}px calc(24px + ${SAFE_AREA.bottom});
		border-radius: ${SHEET_RADIUS}px ${SHEET_RADIUS}px 0 0;
	`}
`;
const Icon = styled.span`
	display: grid;
	place-items: center;
	width: 44px;
	height: 44px;
	margin: 0 auto 10px;
	border-radius: 8px;
	background: #eafce7;
	color: #66b026;
`;
const Title = styled.h2`
	margin: 0 0 8px;
	color: #303030;
	font-size: 24px;
	font-weight: 600;

	${mediaPhone`font-size: 18px;`}
`;
const Copy = styled.p`
	margin: 0;
	color: #454545;
	font-size: 16px;
`;
const Actions = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 20px;
	margin-top: 41px;

	/* Phone: full-width actions, primary on top. */
	${mediaPhone`
		flex-direction: column-reverse;
		align-items: stretch;
		gap: 8px;
		margin-top: 32px;
	`}
`;
const Skip = styled.button`
	width: 86px;
	height: 56px;
	border: 0;
	background: transparent;
	color: rgba(79, 79, 79, 0.88);
	font: inherit;
	font-size: 14px;
	font-weight: 500;
	white-space: nowrap;
	cursor: pointer;

	${mediaPhone`
		width: 100%;
		height: ${TAP_TARGET_PRIMARY}px;
	`}

	${pressedFade}
`;
const Proceed = styled(AppButton)`
	height: 56px;
	padding: 16px 24px;
	font-size: 16px;

	${mediaPhone`
		width: 100%;
		height: ${TAP_TARGET_PRIMARY}px;
	`}
`;

interface SignUpSuccessModalProps {
	accountEmail: string;
	onProceedToOnboarding?: () => void;
}

export const SignUpSuccessModal = ({ accountEmail, onProceedToOnboarding }: SignUpSuccessModalProps) => {
	const [pending, setPending] = useState(() => getSignUpSuccessPending(accountEmail));
	const hasOnboarding = Boolean(onProceedToOnboarding);
	const dismiss = () => {
		clearSignUpSuccessPending();
		setPending(null);
	};
	if (!pending) return null;
	return (
		<Overlay role="presentation">
			<Dialog role="dialog" aria-modal="true" aria-labelledby="signup-success-title">
				<Icon aria-hidden>
					<Check size={24} strokeWidth={2.5} />
				</Icon>
				<Title id="signup-success-title">Account successfully created!</Title>
				<Copy>
					{pending.method === "email" && "Verification code confirmed. "}
					You’re headed to the Creatorverse campaign dashboard
					{hasOnboarding ? " — let’s finish setting up your profile." : "."}
				</Copy>
				<Actions>
					{hasOnboarding && (
						<Skip type="button" onClick={dismiss}>
							Skip for now
						</Skip>
					)}
					<Proceed
						type="button"
						variant="primary"
						onClick={() => {
							dismiss();
							onProceedToOnboarding?.();
						}}
					>
						{hasOnboarding ? "Proceed to onboarding" : "Continue to dashboard"}
					</Proceed>
				</Actions>
			</Dialog>
		</Overlay>
	);
};
